<template>
  <div class="catalog-menu-page">
    <div class="page-block">
      <div class="page-block__head">
        <h1>Меню каталога</h1>
        <div class="page-block__actions">
          <va-button
            color="primary"
            :disabled="saving"
            @click="saveTree"
          >
            {{ saving ? "Сохранение..." : "Сохранить меню" }}
          </va-button>
          <va-button color="info" @click="openAddModal(null)">
            Добавить в корень
          </va-button>
        </div>
      </div>
      <p class="page-desc">
        Элементы могут быть ссылкой (на категорию или произвольный URL) или текстом. У каждого пункта можно задать иконку.
      </p>
      <p class="page-desc page-desc--note">
        Изменения применятся и сохранятся ТОЛЬКО при нажатии «Сохранить меню».
      </p>
    </div>

    <div v-if="tree.length === 0 && !loading" class="page-block">
      <p>Пунктов меню пока нет. Нажмите «Добавить в корень» или «Сохранить меню», если вносили изменения.</p>
    </div>

    <div v-else class="page-block">
      <catalog-menu-tree
        :nodes="tree"
        :categories="categories"
        @edit="openEditModal"
        @add-child="openAddModal"
        @delete="deleteNode"
        @move-up="reorderUp"
        @move-down="reorderDown"
      />
    </div>

    <!-- Модалка оставляем -->
    <div v-show="showModal" class="modal-overlay" @click.self="closeModal">
      <div class="modal">
        <div class="modal__header">
          {{ isEdit ? "Редактировать пункт" : "Новый пункт" }}
        </div>
        <div class="modal__body">
          <catalog-menu-item-form
            v-if="showModal"
            :node="editingNode"
            :categories="categories"
            @submit="onFormSubmit"
            @cancel="closeModal"
          />
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import api from "@/api/axios";
import { mapActions, mapState } from "vuex";
import CatalogMenuTree from "./CatalogMenuTree.vue";
import CatalogMenuItemForm from "./CatalogMenuItemForm.vue";

function buildTreeFromApi(items) {
  if (!items || !Array.isArray(items)) return [];
  return items.map((node) => ({
    id: node.id,
    title: node.title || "",
    type: node.type || "link",
    href: node.href || null,
    category_id: node.category_id || null,
    icon_id: node.icon_id || null,
    icon_url: node.icon_url || null,
    category_slug: node.category_slug || null,
    children: buildTreeFromApi(node.children || []),
  }));
}

function buildPayloadItems(nodes) {
  if (!nodes || !nodes.length) return [];
  return nodes.map((node) => {
    const item = {
      title: node.title,
      type: node.type,
    };
    if (node.href) item.href = node.href;
    if (node.category_id) item.category_id = node.category_id;
    if (node.icon_id) item.icon_id = node.icon_id;
    if (node.children && node.children.length)
      item.children = buildPayloadItems(node.children);
    return item;
  });
}

export default {
  name: "CatalogMenu",
  components: { CatalogMenuTree, CatalogMenuItemForm },
  data() {
    return {
      tree: [],
      loading: true,
      saving: false,
      showModal: false,
      editingNode: null,
      parentForNew: null,
    };
  },
  computed: {
    ...mapState("categories", ["categories"]),
    isEdit() {
      return this.editingNode && this.editingNode.id != null;
    },
  },
  async created() {
    await this.loadCategories();
    await this.fetchTree();
    this.loading = false;
  },
  methods: {
    ...mapActions({
      loadCategories: "categories/getCategories",
    }),
    async fetchTree() {
      try {
        const res = await api.get("/admin/catalog-menu-items");
        const data = res.data && res.data.data != null ? res.data.data : res.data;
        this.tree = buildTreeFromApi(Array.isArray(data) ? data : []);
      } catch (e) {
        console.error("Catalog menu fetch error:", e);
        this.tree = [];
      }
    },
    openEditModal(node) {
      this.editingNode = { ...node };
      this.parentForNew = null;
      this.showModal = true;
    },
    openAddModal(parentNode) {
      this.editingNode = {
        title: "",
        type: "link",
        href: null,
        category_id: null,
        icon_id: null,
        children: [],
      };
      this.parentForNew = parentNode;
      this.showModal = true;
    },
    closeModal() {
      this.showModal = false;
      this.editingNode = null;
      this.parentForNew = null;
    },
    onFormSubmit(formData) {
      if (this.isEdit) {
        const idOrKey = this.editingNode.id != null ? this.editingNode.id : this.editingNode._key;
        this.applyEdit(this.tree, idOrKey, formData);
      } else {
        this.applyAdd(formData, this.parentForNew);
      }
      this.closeModal();
    },
    applyEdit(nodes, idOrKey, data) {
      for (let i = 0; i < nodes.length; i++) {
        const match = nodes[i].id !== undefined && nodes[i].id !== null
          ? nodes[i].id === idOrKey
          : nodes[i]._key === idOrKey;
        if (match) {
          nodes[i].title = data.title;
          nodes[i].type = data.type;
          nodes[i].href = data.href || null;
          nodes[i].category_id = data.category_id || null;
          nodes[i].icon_id = data.icon_id || null;
          if (data.icon_url) nodes[i].icon_url = data.icon_url;
          return true;
        }
        if (nodes[i].children && this.applyEdit(nodes[i].children, idOrKey, data))
          return true;
      }
      return false;
    },
    applyAdd(data, parent) {
      const newNode = {
        _key: "new-" + Date.now(),
        title: data.title,
        type: data.type,
        href: data.href || null,
        category_id: data.category_id || null,
        icon_id: data.icon_id || null,
        icon_url: data.icon_url || null,
        children: [],
      };
      if (!parent) {
        this.tree.push(newNode);
      } else {
        this.ensureChildren(parent);
        parent.children.push(newNode);
      }
    },
    ensureChildren(node) {
      if (!node.children) node.children = [];
    },
    deleteNode(node, parent, indexInParent) {
      if (!confirm("Удалить этот пункт и все подпункты?")) return;
      if (!parent) {
        this.tree.splice(indexInParent, 1);
      } else {
        this.ensureChildren(parent);
        parent.children.splice(indexInParent, 1);
      }
    },
    reorderUp(parent, indexInParent) {
      const list = parent ? (this.ensureChildren(parent), parent.children) : this.tree;
      if (!list || indexInParent <= 0) return;
      const tmp = list[indexInParent];
      this.$set(list, indexInParent, list[indexInParent - 1]);
      this.$set(list, indexInParent - 1, tmp);
    },
    reorderDown(parent, indexInParent) {
      const list = parent ? (this.ensureChildren(parent), parent.children) : this.tree;
      if (!list || indexInParent < 0 || indexInParent >= list.length - 1) return;
      const tmp = list[indexInParent];
      this.$set(list, indexInParent, list[indexInParent + 1]);
      this.$set(list, indexInParent + 1, tmp);
    },
    async saveTree() {
      this.saving = true;
      try {
        const payload = { items: buildPayloadItems(this.tree) };
        await api.put("/admin/catalog-menu/tree", payload);
        await this.fetchTree();
        alert("Сохранено");
      } catch (e) {
        console.error("Save catalog menu error:", e);
        const msg = e.response && e.response.data && e.response.data.message
          ? e.response.data.message
          : "Ошибка сохранения";
        alert(msg);
      } finally {
        this.saving = false;
      }
    },
  },
};
</script>

<style scoped>
.catalog-menu-page {
  padding: 0;
}
.catalog-menu-page h1 {
  margin: 0;
}
.page-block {
  margin-bottom: 1.5rem;
}
.page-block__head {
  display: flex;
  align-items: center;
  justify-content: space-between;
  flex-wrap: wrap;
  gap: 0.5rem;
}
.page-block__actions {
  display: flex;
  gap: 0.5rem;
}
.page-desc {
  margin: 0.5rem 0 0;
  color: #666;
  font-size: 0.9rem;
}

/* Модалка — оставляем как есть */
.modal-overlay {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.4);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1000;
}
.modal {
  background: #fff;
  border-radius: 8px;
  min-width: 400px;
  max-width: 90vw;
  max-height: 90vh;
  overflow: auto;
  box-shadow: 0 4px 20px rgba(0, 0, 0, 0.15);
}
.modal__header {
  padding: 1rem 1.25rem;
  border-bottom: 1px solid #e0e0e0;
  font-weight: 600;
}
.modal__body {
  padding: 1.25rem;
}
</style>
